/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import React, {useEffect, useState} from "react";
import styled from "styled-components";
import {usePostRequest} from "../../../../api/helper";
import JSZip from "jszip";
import Loader from "../../../components/Loader/Loader";
import {Pagination} from '@mui/material';
import {SCENARIO_ANALYSIS_CONSTANTS} from "../constants";
import toast from "react-hot-toast";
import ModelNotSelected from "./ModelNotSelected";


interface DescriptionProps {
  model: string
  variation: any
  version: any
}


const Container = styled.div({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  height: '100%',
  width: '100%',
  '> *': {
    display: 'flex',
    justifyContent: 'center'
  }
})


const ImageContainer = styled.div({
  flex: 1,
  width: '100%',
  height: '100%',
  overflow: 'auto',
})


const PaginationContainer = styled.div({marginTop: 5})


/**
 * Renders the Description tab content on the View Model page
 */
const Description = (props: DescriptionProps) => {
  const {model, variation, version} = props

  const [isFetchModelDescriptionLoading, setIsFetchModelDescriptionLoading] = useState<boolean>(false)
  const [page, setPage] = React.useState(1)  /* Selected page. Every model has at least 1 page. */
  const [imgUrls, setImgUrls] = useState<string[]>([])

  /**
   * Update selected page value
   */
  const handlePaginationChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value)
  }

  /**
   * Extract images from the received zip file
   */
  const extractImages = async (data: any) => {
    var zip = new JSZip()
    const bin = data?.images
    const tzip = await zip.loadAsync(bin, {base64: true})

    const oImages = tzip.files
    const imageNames = Object.keys(oImages)
    let objectUrls = []
    for (const value of imageNames) {
      const _blob: any = await tzip?.file(value)?.async("blob")
      const objectURL: string = URL.createObjectURL(_blob)
      objectUrls.push(objectURL)
    }
    setImgUrls(objectUrls)
    setIsFetchModelDescriptionLoading(false)
  }

  /**
   * Fetch model description content
   */
  const modelDescriptionUrl = SCENARIO_ANALYSIS_CONSTANTS.viewModel.endpoints.modelDescription
  const fetchModelDescriptionHandle = usePostRequest(modelDescriptionUrl)
  const fetchModelDescription = async () => {
    setIsFetchModelDescriptionLoading(true)
    const body = {model, version, variation}
    fetchModelDescriptionHandle(body).then((data) => {
      extractImages(data)
    }).catch((e) => {
      console.log(e)
      toast.error("An error occurred while fetching the Description content")
    }).then(() => {
      setPage(1)  /* Reset to first page after model change */
      setIsFetchModelDescriptionLoading(false)
    })
  }

  /**
   * Fetch and set the model's Description tab when a different model, version, and 
   * variation is selected
   */
  useEffect(() => {
    if (!model || !version ||!variation) return
    fetchModelDescription()
  }, [model, version, variation])

  return (
    <Container>
      {(!model || !version || !variation) ? 
        <ModelNotSelected/>
      : <Loader isLoading={isFetchModelDescriptionLoading}>
        <ImageContainer>
          <img style={{height: '100%'}} src={imgUrls[page - 1]}/>
        </ImageContainer>
        <PaginationContainer>
          <Pagination count={imgUrls.length} page={page} onChange={handlePaginationChange}/>
        </PaginationContainer>
      </Loader>}
    </Container>
  )
}

export default Description
